import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import "driver.js/dist/driver.css";
import { actions as userActions } from "store/users";
import {
    FORCE_TOURS,
    TOUR_REPLAY_START_DELAY_MS,
    createTourDriver,
    isTourShown,
    useTourReplayGeneration,
} from "utils/tours";

const { saveUserMeta } = userActions;

let transportationTourHasRun = false;
let lastHandledReplayGeneration = 0;

const TRANSPORTATION_TOUR_STEPS = [
    {
        element: "[data-transportation-tour='commute-distance']",
        popover: {
            title: "Round-trip Daily Commute Distance",
            description:
                "The total distance driven for your daily commute. This affects when and how much charging is needed to cover a trip.",
        },
    },
    {
        element: "[data-transportation-tour='general-specifications']",
        popover: {
            title: "General Specifications",
            description:
                "Select a vehicle or a generic option based on low, mid, or high performance within their category. The remaining fields will populate automatically.",
        },
    },
    {
        element: "[data-transportation-tour='vehicle-specifications']",
        popover: {
            title: "Vehicle Specifications",
            description:
                "Auto-filled based on the selected vehicle, including motor output, transmission, fuel economy, and emissions ratings. CO₂ Rating and Smog Rating are on a 1-10 scale, with 10 being best.",
        },
    },
    {
        element: "[data-transportation-tour='vehicle-settings']",
        popover: {
            title: "Vehicle Settings",
            description:
                "Set upper and lower charge limits and starting conditions for the vehicle, with some values preset based on average values.",
        },
    },
    {
        element: "[data-transportation-tour='schedule-hours']",
        popover: {
            title: "Driving Schedule",
            description: "Set up a schedule or use the preset for an average 9am-5pm commute period.",
        },
    },
    {
        element: "[data-transportation-tour='charge-program']",
        popover: {
            title: "Charge Program",
            description: "Select a utility or grid program to apply to your charging simulation.",
        },
    },
    {
        element: "[data-transportation-tour='charge-preference']",
        popover: {
            title: "Charge Preference",
            description:
                "Choose how charging is controlled: Uncontrolled charges as soon as the vehicle is plugged in, while other options optimize charging timing based on cost or grid conditions.",
        },
    },
    {
        element: "[data-transportation-tour='v2g-options']",
        popover: {
            title: "Vehicle-to-Grid Options",
            description:
                "Toggle this to define bi-directional charging behaviour and change the hours when your vehicle can be discharged to provide grid support (e.g. peak shaving).",
        },
    },
    {
        element: "[data-transportation-tour='tou-options']",
        popover: {
            title: "Time-of-Use Options",
            description:
                "Set up a Time-of-Use (TOU) pricing schedule in the General tab or use one of the available presets. This can help more accurately estimate electricity costs, and be used for arbitrage V2G strategies.",
        },
    },
];

function scrollTourElementIntoView(element) {
    if (!element) return;
    element.scrollIntoView({ block: "center", behavior: "smooth" });
}

export default function TransportationTour({ hasElectricVehicle = false, onTourStart, onTourEnd } = {}) {
    const dispatch = useDispatch();
    const uid = useSelector((state) => state.user?.uid);
    const replayGeneration = useTourReplayGeneration("transportation");
    const transportationTourShown = useSelector((state) => state.user?.userMeta?.transportationTourShown);

    useEffect(() => {
        // Every step targets the EV form, so stay pending (including replays) until one exists.
        if (!hasElectricVehicle) return;

        const isReplay = replayGeneration > lastHandledReplayGeneration;
        if (isReplay) {
            lastHandledReplayGeneration = replayGeneration;
            transportationTourHasRun = false;
        }
        if (!isReplay && isTourShown(transportationTourShown)) return;
        if (transportationTourHasRun) return;

        let cancelled = false;
        let tourDriver = null;
        // Wait on replay so clearing transportationTourShown can settle before drive().
        const startDelayMs = replayGeneration > 0 ? TOUR_REPLAY_START_DELAY_MS : 2000;

        const startTour = () => {
            if (cancelled || transportationTourHasRun) return;
            transportationTourHasRun = true;
            onTourStart?.();

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (cancelled) {
                        transportationTourHasRun = false;
                        onTourEnd?.();
                        return;
                    }
                    // Wait for accordion Collapse to mount EV form children after forceOpen.
                    setTimeout(
                        () => {
                            if (cancelled) {
                                transportationTourHasRun = false;
                                onTourEnd?.();
                                return;
                            }
                            const steps = TRANSPORTATION_TOUR_STEPS.filter((s) => document.querySelector(s.element));
                            if (steps.length === 0) {
                                transportationTourHasRun = false;
                                onTourEnd?.();
                                return;
                            }
                            const firstStepElement = document.querySelector(steps[0]?.element);
                            scrollTourElementIntoView(firstStepElement);
                            const shouldPersist = uid && !FORCE_TOURS;
                            tourDriver = createTourDriver({
                                stepCount: steps.length,
                                onHighlightStarted: (element) => {
                                    scrollTourElementIntoView(element);
                                },
                                onDestroyed: () => {
                                    onTourEnd?.();
                                    if (shouldPersist) {
                                        dispatch(saveUserMeta({ uid, userMeta: { transportationTourShown: true } }));
                                    }
                                },
                            });
                            tourDriver.setSteps(steps);
                            const startDrive = () => {
                                if (cancelled) return;
                                tourDriver.drive();
                            };
                            if (firstStepElement) {
                                setTimeout(startDrive, isReplay ? 0 : 300);
                            } else {
                                startDrive();
                            }
                        },
                        isReplay ? 100 : 400
                    );
                });
            });
        };

        const timeoutId = setTimeout(startTour, startDelayMs);
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
            if (tourDriver) {
                tourDriver.suppressReplayHint?.();
                tourDriver.destroy();
                tourDriver = null;
            }
            onTourEnd?.();
            transportationTourHasRun = false;
        };
    }, [transportationTourShown, dispatch, uid, replayGeneration, hasElectricVehicle, onTourStart, onTourEnd]);

    return null;
}
